import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, LinearProgress } from '@mui/material';
import moment from 'moment/moment';

import useNotification from '../../../../../core/hooks/use_notification';
import { getAllProductProductsNotificationData, getHubsNotificationData, getPopsNotificationData, getReservationsNotificationData } from '../../../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import { getReservationsAction, getReservationsState } from '../../../../../features/reservation/get_reservations';
import { getDefaultPaginationQueryValues } from '../../../../../core/utils/api_utils';
import ReservationFilters from './reservation_filters';
import ReservationsList from './reservations_list';
import { ReservationStatusEnum } from '../../../../../core/enums/reservation/reservation';
import PaginationControls from '../../../../common/pagination_controls';
import { getAllHubsAction, getAllHubsState } from '../../../../../features/pop/get_all_hubs';
import { getAllPopsAction, getAllPopsState } from '../../../../../features/pop/get_all_pops';
import { getAllProductsAction, getAllProductsState } from '../../../../../features/product/get_all_products';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues,
    languageCode: 'PL',
    from: moment().startOf('day'),
    to: moment().startOf('day').add(10, 'days'),
    reservationStatuses: [ReservationStatusEnum.PAID.value, ReservationStatusEnum.PREPARED.value],
    hubIds: [],
    productId: ''
};

const Reservation = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getAllPops = useSelector(getAllPopsState);
    const getAllHubs = useSelector(getAllHubsState);
    const getAllProducts = useSelector(getAllProductsState);
    const getReservations = useSelector(getReservationsState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    const refreshReservations = useCallback(() => {
        const params = {
            ...queryParams,
            from: queryParams?.from?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
            to: queryParams?.to?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT)
        };

        dispatch(getReservationsAction(params))
            .then(response => showErrorMessage(getReservationsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshReservations();
    }, [refreshReservations]);

    useEffect(() => {
        dispatch(getAllHubsAction())
            .then(response => showErrorMessage(getHubsNotificationData(response)));
        dispatch(getAllPopsAction())
            .then(response => showErrorMessage(getPopsNotificationData(response)));
        dispatch(getAllProductsAction())
            .then(response => showErrorMessage(getAllProductProductsNotificationData(response)));
    }, [dispatch, showErrorMessage]);

    const isLoading = getAllHubs?.loading || getAllPops?.loading || getReservations?.loading;

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 3, position: 'relative', pt: 1 } }>

            <Box sx={ { position: 'absolute', top: -8, left: 0, width: '100%', zIndex: 10, height: 4 } }>
                { isLoading && <LinearProgress/> }
            </Box>

            { (getAllHubs?.data && getAllProducts?.data) && (
                <ReservationFilters
                    from={ queryParams.from }
                    onChangeTimeFrom={ from => setQueryParams(prevState => ({ ...prevState, from })) }
                    to={ queryParams.to }
                    onChangeTimeTo={ to => setQueryParams(prevState => ({ ...prevState, to })) }
                    onSelectReservationStatusesChange={ reservationStatuses => setQueryParams(prevState => ({
                        ...prevState,
                        reservationStatuses
                    })) }
                    onSelectHubsChange={ hubIds => setQueryParams(prevState => ({ ...prevState, hubIds })) }
                    onProductChange={ productId => setQueryParams(prevState => ({ ...prevState, productId })) }
                />
            ) }

            { (getAllHubs?.data && getAllPops?.data && getReservations?.data) && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <ReservationsList refreshReservations={ refreshReservations }/>

                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getReservations?.data?.pageable?.pageNumber }
                            totalPages={ getReservations?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => ({ ...prevState, page })) }
                        />
                    </Box>
                </Box>
            ) }

        </Box>
    );
};

export default Reservation;